import { Inject, inject, Injectable } from '@angular/core';
import {
  getError,
  getErrorInstance,
  sanitiseRecaptchaAction,
} from '@principle-theorem/shared';
import { injectScript } from '../inject-script';
import { IRecaptchaConfig, RECAPTCHA_CONFIG } from './recaptcha-config';
import { DOCUMENT } from '@angular/common';

declare const grecaptcha: {
  enterprise: {
    ready: (callback: () => Promise<void>) => void;
    execute: (keyId: string, options: { action: string }) => Promise<string>;
  };
};

@Injectable({ providedIn: 'root' })
export class RecaptchaService {
  private static _isReady: Promise<void> | undefined;
  private _window: Window | undefined;
  private _config = inject<IRecaptchaConfig>(RECAPTCHA_CONFIG);

  constructor(@Inject(DOCUMENT) private _document: Document) {
    this._window = this._document.defaultView ?? undefined;
  }

  async execute(action: string): Promise<string> {
    if (!RecaptchaService._isReady) {
      RecaptchaService._isReady = this._loadRecaptchaScript();
    }

    try {
      await RecaptchaService._isReady;
    } catch (error) {
      RecaptchaService._isReady = undefined;
      throw error;
    }

    return this._run(() =>
      grecaptcha.enterprise.execute(this._config.siteKey, {
        action: sanitiseRecaptchaAction(action),
      })
    );
  }

  private async _run<T>(callback: () => Promise<T>): Promise<T> {
    if (typeof grecaptcha === 'undefined' || !grecaptcha?.enterprise) {
      throw new Error('reCAPTCHA not available after script load');
    }

    return new Promise((resolve, reject) => {
      try {
        grecaptcha.enterprise.ready(async () => {
          try {
            const result = await callback();
            resolve(result);
          } catch (err) {
            reject(getErrorInstance(err));
          }
        });
      } catch (err) {
        reject(getErrorInstance(err));
      }
    });
  }

  private async _loadRecaptchaScript(): Promise<void> {
    if (!this._window || !this._document) {
      return;
    }

    const window = this._window as unknown as {
      grecaptcha?: typeof grecaptcha;
    };
    if (window.grecaptcha?.enterprise) {
      return;
    }

    if (this._document.getElementById('recaptcha')) {
      return;
    }

    try {
      await injectScript(
        'recaptcha',
        `https://www.google.com/recaptcha/enterprise.js?render=${this._config.siteKey}`,
        'text/javascript',
        true,
        false
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(getError(error));
      throw error;
    }
  }
}
